import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import { CropWorkType } from '../../../../../enums/crop-work-types';
import { CROP_WORK_CREATE } from '../../../../../router/routes';
import { route } from '../../../../../services/utils';
import css from './action-buttons.module.css';
import dispersionOrSpraying from './icons/dispersion_or_spraying.svg';
import forageHarvesting from './icons/forage_harvesting.svg';
import fruitHarvesting from './icons/fruit_harvesting.svg';
import mowing from './icons/mowing.svg';
import other from './icons/other.svg';
import soilTreatment from './icons/soil_treatment.svg';
import sowingOrPlanting from './icons/sowing_or_planting.svg';
import threshing from './icons/threshing.svg';
import vegetableEtcHarvesting from './icons/vegetable_etc_harvesting.svg';

export default function ActionButtons({
  enabledButtons = null,
  fields = null,
}: {
  enabledButtons?: CropWorkType[];
  fields?: string[];
}) {
  const { t } = useTranslation();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();

  interface Button {
    id: CropWorkType;
    tooltip: string;
    img: string;
    text: string;
  }

  const buttons: Button[] = [
    {
      id: CropWorkType.SOIL_TREATMENT,
      tooltip: 'soil_treatment_tooltip',
      img: soilTreatment,
      text: 'classifiers.properties.crop_work_type.soil_treatment',
    },
    {
      id: CropWorkType.SOWING_OR_PLANTING,
      tooltip: 'sowing_or_planting_tooltip',
      img: sowingOrPlanting,
      text: 'classifiers.properties.crop_work_type.sowing_or_planting',
    },
    {
      id: CropWorkType.DISPERSION_OR_SPRAYING,
      tooltip: 'dispersion_or_spraying_tooltip',
      img: dispersionOrSpraying,
      text: 'classifiers.properties.crop_work_type.dispersion_or_spraying',
    },
    {
      id: CropWorkType.MOWING,
      tooltip: 'mowing_tooltip',
      img: mowing,
      text: 'classifiers.properties.crop_work_type.mowing',
    },
    {
      id: CropWorkType.THRESHING,
      tooltip: 'threshing_tooltip',
      img: threshing,
      text: 'classifiers.properties.crop_work_type.threshing',
    },
    {
      id: CropWorkType.FORAGE_HARVESTING,
      tooltip: 'forage_harvesting_tooltip',
      img: forageHarvesting,
      text: 'classifiers.properties.crop_work_type.forage_harvesting',
    },
    {
      id: CropWorkType.FRUIT_HARVESTING,
      tooltip: 'fruit_harvesting_tooltip',
      img: fruitHarvesting,
      text: 'classifiers.properties.crop_work_type.fruit_harvesting',
    },
    {
      id: CropWorkType.VEGETABLE_ETC_HARVESTING,
      tooltip: 'vegetable_etc_harvesting_tooltip',
      img: vegetableEtcHarvesting,
      text: 'classifiers.properties.crop_work_type.vegetable_etc_harvesting',
    },
    {
      id: CropWorkType.OTHER,
      tooltip: 'other_tooltip',
      img: other,
      text: 'classifiers.properties.crop_work_type.other',
    },
  ];

  function linkClassName(to: CropWorkType) {
    if (enabledButtons === null || enabledButtons.includes(to)) {
      return css['btn'];
    } else {
      return css['btn'].concat(' ', css['disabled']);
    }
  }

  return (
    <div className={css['container']}>
      {buttons.map((button) => (
        <Link
          key={button.id}
          data-tip
          data-for={button.tooltip}
          className={linkClassName(button.id)}
          to={route(CROP_WORK_CREATE.concat('?fields=:fields'), {
            farmId: farmId,
            season: season,
            type: button.id,
            fields: fields,
          })}
        >
          <span className={css['icon']}>
            <img alt="" src={button.img} />
          </span>
          <ReactTooltip
            id={button.tooltip}
            backgroundColor={'#002A58'}
            place="top"
            effect="solid"
          >
            {t(button.text)}
          </ReactTooltip>
        </Link>
      ))}
    </div>
  );
}
